import Phaser from 'phaser';
import {
  capForPath,
  copyUnpaintedPoses,
  duelArtFiles,
  missingArt,
  missingReferencedTextures,
  queueArt,
  releaseChestPlaceholder,
  titleArtFiles,
  type ArtFile,
} from './assets';
import { pinCanvas } from './charmArtLoad';
import { menuArtFiles } from './menuArt';
import { yieldToPaint } from './yieldPaint';

const MAX_ATTEMPTS = 3;
const INFLIGHT_MS = 90_000;

/**
 * Title, duel, and menu batches share one Phaser loader.
 * Two start() calls at once drop the webp queue, and the retry then fetches the png.
 */
let loaderChain: Promise<void> = Promise.resolve();

function enqueueLoad<T>(job: () => Promise<T>): Promise<T> {
  const run = loaderChain.then(job, job);
  loaderChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

let duelInflight: Promise<void> | null = null;
let menuInflight: Promise<void> | null = null;
const lateHook = new WeakSet<object>();

function noteMissing(label: string, textures: { exists(key: string): boolean }, keys: readonly string[]): void {
  const missing = keys.filter((key) => !textures.exists(key));
  if (missing.length > 0) console.warn(`${label}: ${missing.join(', ')}`);
}

function releaseBlockedChests(scene: Phaser.Scene, files: readonly ArtFile[]): void {
  for (const file of files) {
    if (file.pack === 'chest') releaseChestPlaceholder(scene, file.key);
  }
}

interface Measured {
  width?: number;
  height?: number;
}

/** Shrink one uploaded painting. Returns true when a canvas copy was drawn. */
function clampOne(scene: Phaser.Scene, file: ArtFile): boolean {
  if (typeof document === 'undefined') return false;
  if (!scene.textures.exists(file.key)) return false;
  const cap = capForPath(file.path);
  const source = scene.textures.get(file.key).getSourceImage() as CanvasImageSource & Measured;
  const width = source.width ?? 0;
  const height = source.height ?? 0;
  if (width <= cap && height <= cap) return false;
  if (width < 2 || height < 2) return false;
  const scale = Math.min(cap / width, cap / height);
  const tw = Math.max(1, Math.round(width * scale));
  const th = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(source, 0, 0, tw, th);
  scene.textures.remove(file.key);
  scene.textures.addImage(file.key, canvas as unknown as HTMLImageElement);
  pinCanvas(canvas);
  return true;
}

/** Shrink uploaded paintings that would blow a phone GPU texture limit. */
export function clampLoadedTextures(scene: Phaser.Scene, files: readonly ArtFile[]): void {
  for (const file of files) clampOne(scene, file);
}

/** Same shrink, but paint a frame between canvases so the glen loops keep moving. */
async function clampLoadedTexturesGentle(scene: Phaser.Scene, files: readonly ArtFile[]): Promise<void> {
  for (const file of files) {
    if (clampOne(scene, file)) await yieldToPaint();
  }
}

let clampChain: Promise<void> = Promise.resolve();

/** Run canvas shrinks one after another, never piled into the same turn. */
function queueTextureClamp(scene: Phaser.Scene, files: readonly ArtFile[]): Promise<void> {
  const job = clampChain.then(() => clampLoadedTexturesGentle(scene, files));
  clampChain = job.then(
    () => undefined,
    () => undefined,
  );
  return job;
}

function whenIdle(scene: Phaser.Scene, cap: number): Promise<void> {
  if (scene.load.isReady()) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      scene.load.off('complete', finish);
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, cap);
    scene.load.on('complete', finish);
  });
}

function loadOnce(scene: Phaser.Scene, files: readonly ArtFile[], attempt: number): Promise<void> {
  return enqueueLoad(() =>
    whenIdle(scene, INFLIGHT_MS).then(
      () =>
        new Promise((resolve) => {
          let settled = false;
          let timer = 0;
          const finish = () => {
            if (settled) return;
            settled = true;
            scene.load.off('complete', onComplete);
            if (timer) window.clearTimeout(timer);
            resolve();
          };
          const onComplete = () => finish();
          releaseBlockedChests(scene, files);
          const left = files.filter((file) => !scene.textures.exists(file.key));
          if (left.length === 0 || !scene.load.isReady()) {
            finish();
            return;
          }
          // Stay on this attempt until the batch finishes. A short timer used to
          // start the original png while the packed file was still downloading.
          timer = window.setTimeout(finish, INFLIGHT_MS);
          scene.load.on('complete', onComplete);
          queueArt(scene, left, attempt);
          scene.load.start();
        }),
    ),
  );
}

function ensureFiles(scene: Phaser.Scene, files: readonly ArtFile[]): Promise<void> {
  const run = (attempt: number): Promise<void> => {
    releaseBlockedChests(scene, files);
    if (missingArt(scene.textures, files).length === 0) return Promise.resolve();
    if (attempt > MAX_ATTEMPTS) return Promise.resolve();
    return loadOnce(scene, files, attempt).then(() => run(attempt + 1));
  };
  return run(1).then(() => queueTextureClamp(scene, files));
}

function watchLateArt(scene: Phaser.Scene, files: readonly ArtFile[]): void {
  if (lateHook.has(scene)) return;
  lateHook.add(scene);
  scene.load.on('complete', () => {
    releaseBlockedChests(scene, files);
    void queueTextureClamp(scene, files).then(() => {
      copyUnpaintedPoses(scene);
    });
  });
}

/** Retry title paintings that failed during boot. The chest is in this batch, not the duel queue. */
export function ensureTitleArt(scene: Phaser.Scene): Promise<void> {
  const files = titleArtFiles();
  return ensureFiles(scene, files).then(() => {
    if (scene.load.isReady()) noteMissing('Title textures still missing', scene.textures, files.map((file) => file.key));
  });
}

/**
 * Retry duel paintings, including cosmetics and critter poses.
 * A board that loaded on the first pass does not count as the rest being ready.
 */
export function ensureDuelArt(scene: Phaser.Scene): Promise<void> {
  const files = duelArtFiles();
  watchLateArt(scene, files);
  const settle = () => {
    copyUnpaintedPoses(scene);
    if (scene.load.isReady()) noteMissing('Textures still missing', scene.textures, missingReferencedTextures(scene.textures));
  };
  releaseBlockedChests(scene, files);
  if (missingArt(scene.textures, files).length === 0) {
    return queueTextureClamp(scene, files).then(settle);
  }
  if (duelInflight) return duelInflight;
  duelInflight = ensureFiles(scene, files)
    .then(settle)
    .finally(() => {
      duelInflight = null;
    });
  return duelInflight;
}

/** Event banners, the wheel, and the prize pictures. Loaded with the glen, not on first open. */
export function ensureMenuArt(scene: Phaser.Scene): Promise<void> {
  const files = menuArtFiles();
  if (missingArt(scene.textures, files).length === 0) {
    return queueTextureClamp(scene, files);
  }
  if (menuInflight) return menuInflight;
  menuInflight = ensureFiles(scene, files).finally(() => {
    menuInflight = null;
  });
  return menuInflight;
}
